import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { deepEqual } from 'fast-equals';

import { CheckboxComponent, DividerComponent } from '@recruitee/user-interface';

import type { Ability, Role, RoleAbilityMap } from '../types';

@Component({
  selector: 'rti-role-ability-matrix',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './role-ability-matrix.component.html',
  styleUrls: ['./role-ability-matrix.component.less'],
  imports: [CheckboxComponent, DividerComponent],
})
export class RoleAbilityMatrixComponent {
  public static buildRolesForm(
    abilities: Ability[] | null,
    companyRoles: Role[],
    integrationRoles: RoleAbilityMap,
  ): RoleAbilityMap {
    const nameToAbilityMap = abilities!.reduce(
      (acc, ability) => {
        acc[ability.name] = ability;
        return acc;
      },
      <{ [name: string]: Ability }>{},
    );

    return companyRoles.reduce((form, role) => {
      const filledAbilities = form[role.id];

      if (filledAbilities) {
        return filledAbilities.reduce((acc, abilityName) => {
          const ability = nameToAbilityMap[abilityName];

          if (!ability) return acc;

          return this.addRoleAbilityToMap(acc, role, ability);
        }, form);
      } else {
        return { ...form, [role.id]: [] };
      }
    }, integrationRoles);
  }

  public static roleAbilityMapChanged(
    initialRoleAbilityMap: RoleAbilityMap,
    roleAbilityMap: RoleAbilityMap,
  ): boolean {
    const existentRoleIds = Object.keys(initialRoleAbilityMap);
    const desiredRoleIds = Object.keys(roleAbilityMap);

    if (!this.setsEqual(existentRoleIds, desiredRoleIds)) {
      return true;
    }

    return existentRoleIds.some(existentRoleId => {
      const existentRoleAbilities = roleAbilityMap[existentRoleId];
      const desiredRoleAbilities = initialRoleAbilityMap[existentRoleId];
      return !this.setsEqual(existentRoleAbilities, desiredRoleAbilities);
    });
  }

  public static setsEqual(arr1: any[], arr2: any[]): boolean {
    return deepEqual([...arr1].sort(), [...arr2].sort());
  }

  private static addRoleAbilityToMap(
    map: RoleAbilityMap,
    role: Role,
    ability: Ability,
  ): RoleAbilityMap {
    const roleAbilities = map[role.id] || [];
    const newRoleAbilities = Array.from(
      new Set(roleAbilities.concat(ability.name, ...ability.requires)),
    );

    return { ...map, [role.id]: newRoleAbilities };
  }

  @Input() public roles: Role[];
  @Input() public abilities: Ability[] | null;
  @Input() public roleAbilityMap: RoleAbilityMap;
  @Input() public disabled: boolean = false;

  @Output() public roleAbilityMapUpdated: EventEmitter<RoleAbilityMap> =
    new EventEmitter<RoleAbilityMap>();

  public roleTrackBy(_index: number, role: Role): string {
    return String(role.id);
  }

  public abilityTrackBy(_index: number, ability: Ability): string {
    return ability.name;
  }

  public roleAbilityChecked(role: Role, ability: Ability): boolean {
    return (
      this.roleAbilityMap[role.id] &&
      this.roleAbilityMap[role.id].some(roleAbility => roleAbility === ability.name)
    );
  }

  public setRoleAbility(role: Role, ability: Ability, value: boolean): void {
    const updatedRoleAbilityMap = value
      ? RoleAbilityMatrixComponent.addRoleAbilityToMap(this.roleAbilityMap, role, ability)
      : this.removeRoleAbilityFromMap(this.roleAbilityMap, role, ability);

    this.roleAbilityMapUpdated.emit(updatedRoleAbilityMap);
  }

  private removeRoleAbilityFromMap(
    map: RoleAbilityMap,
    role: Role,
    ability: Ability,
  ): RoleAbilityMap {
    const dependants = this.abilities!.filter(potentiallyDependantAbility =>
      potentiallyDependantAbility.requires.some(
        requiredAbility => requiredAbility === ability.name,
      ),
    ).map(dependantAbility => dependantAbility.name);

    const abilitiesToRemove = dependants.concat(ability.name);

    const roleAbilities = map[role.id] || [];
    const newRoleAbilities = roleAbilities.filter(
      roleAbility => !abilitiesToRemove.some(abilityToRemove => abilityToRemove === roleAbility),
    );

    return { ...map, [role.id]: newRoleAbilities };
  }
}
